import React, { useRef } from 'react';
import { m, useInView } from 'framer-motion';

import Box from '@mui/material/Box';
import Image from 'next/image';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material';

import { CONFIG } from 'src/global-config';
import { AnimateCountUp } from 'src/components/animate';
import { useLocale } from 'src/locales';
import { AboutInnovation } from './about-innovation';
import { AboutStoryInnovation } from './about-story-innovation';

// ----------------------------------------------------------------------

const SUMMARY = [
  { name: 'Clients', number: 5000 },
  { name: 'Orders', number: 45000 },
  { name: 'Search Inquiries', number: 150000 },
  { name: 'Years Experience', number: 5 },
];

// ----------------------------------------------------------------------

export function AboutOverView({ sx, ...other }) {
  const { t } = useLocale();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const language = localStorage.getItem('i18nextLng') || 'ar';

  const renderHead = () => (
    <m.div
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          component="h1"
          sx={{ fontSize: '2rem !important', mb: 3, mt: { xs: 3, md: 10 } }}
        >
          {t('A_Story_of_Innovation_Values_of_Discipline_and_Excellence')}
        </Typography>
        <Typography
          sx={{ mx: 'auto', maxWidth: 800, color: 'text.secondary', textAlign: 'justify' }}
        >
          {t(
            'Shahen_is_a_Saudi_platform_specialized_in_managing_and_operating_enterprise_land_transport'
          )}
        </Typography>
      </Box>
    </m.div>
  );

  const renderImages = () =>
    IMAGES.map((item, index) => (
      <Grid key={item.src} size={{ xs: 12, sm: 12, md: index === 0 ? 6 : 2 }}>
        <m.div
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 * index, ease: 'easeOut' }}
        >
          <Box
            component="img"
            src={item.src}
            alt={item.alt}
            sx={{
              display: 'block',
              width: { xs: '100%', md: 1 },
              height: { xs: 200, sm: 300, md: 360 },
              maxWidth: { xs: 300, sm: 400, md: 1 },
              maxHeight: { xs: 200, sm: 300, md: 360 },
              mx: { xs: 'auto', sm: 'auto', md: 0 },
              borderRadius: 2,
              objectFit: 'cover',
              objectPosition: {
                md:
                  index === 0
                    ? 'center center'
                    : index === IMAGES?.length - 1
                      ? language === 'ar'
                        ? 'left center'
                        : 'right center'
                      : `calc(${index % 2 === 0 ? 75 : 60}% - ${index % 2 === 0 ? 20 : 10}px) center`,
              },

              ...(index !== 0 && { display: { xs: 'none', md: 'block' } }),
            }}
          />
        </m.div>
      </Grid>
    ));

  const renderRightTexts = () => (
    <m.div
      initial={{ opacity: 0, x: -40 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <Box
        sx={{
          mb: 2,
          width: 24,
          height: 3,
          borderRadius: 3,
          bgcolor: 'primary.main',
          mx: { xs: 'auto', md: 0 },
        }}
      />
      <Typography variant="h3" sx={{ textAlign: { xs: 'center', md: 'left' } }}>
        {t('About_title')}
      </Typography>
    </m.div>
  );

  const renderLeftTexts = () => (
    <m.div
      initial={{ opacity: 0, x: 40 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.4 }}
    >
      <Typography sx={{ color: 'text.secondary', textAlign: { xs: 'center', md: 'left' } }}>
        {t('About_Title_desc')}
      </Typography>
    </m.div>
  );
  const IMAGES = [
    {
      src: `${CONFIG.assetsDir}/assets/images/home/truck-images/traila/تريلا-ثلاجة-تبريد-تجميد-Reefer-Refrigerated-Trailer.webp`,
      alt: t('Feker_for_Communications_and_IT_the_parent_company_of_Shahen_brand'),
    },
    {
      src: `${CONFIG.assetsDir}/assets/images/home/truck-images/dyna/دينا-صندوق-جاف-مغلق-Dyna-Box-Truck.webp`,
      alt: t('Leading_land_transport_companies_in_Saudi_Arabia_licensed_by_TGA'),
    },
    {
      src: `${CONFIG.assetsDir}/assets/images/home/truck-images/six/سقس-شبك-جوانب-عالية-Six-Wheel-Cage-Truck.webp`,
      alt: t('A_smart_logistics_platform_developing_the_logistics_sector_in_the_Kingdom'),
    },
    {
      src: `${CONFIG.assetsDir}/assets/images/home/truck-images/pickup/ونيت-بك-اب-نقل-خفيف-Pickup-Truck.webp`,
      alt: t('Shahen_logistics_services_team_in_Riyadh'),
    },
  ];

  return (
    <Box
      ref={ref}
      component="section"
      sx={[
        {
          pb: 5,
          pt: { xs: 3, md: 5 },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 10 } }}>
        {renderHead()}

        <Grid container spacing={{ xs: 0, sm: 3 }}>
          {renderImages()}
        </Grid>
        {/*
        <Grid
          container
          spacing={{ xs: 5, md: 3 }}
          sx={{ justifyContent: 'space-between', textAlign: { xs: 'center', md: 'left' } }}
        >
          <Grid size={{ xs: 12, md: 6, lg: 5 }}>{renderRightTexts()}</Grid>
          <Grid size={{ xs: 12, md: 6, lg: 6 }}>{renderLeftTexts()}</Grid>
        </Grid> */}
        {/* <AboutInnovation /> */}
        <AboutStoryInnovation />
      </Container>
    </Box>
  );
}
